// filepath: apps/web/src/features/directory/types.ts
//
// FR-UI-DIR-1 -- wire types for the People and Organization Directory
// Workspace. Mirrors the Go wire types in `internal/directory/**`
// (FR-DIR-1, FR-DIR-2).
//
// INV-1: every cache key downstream includes tenantSlug.
// INV-2: types describe server-stripped shapes only; the SPA MUST NOT
//        rebuild fields the server stripped. There are no
//        `displayName?: string` synthesis helpers here on purpose --
//        the server is the single source of truth for what is
//        visible (FR-AUTH-6 / `entity.Authorizer.StripFields`).
// INV-9: all string fields are React text-node rendered.

/** Common row_version field (OCC token) on every mutable record. */
export interface RowVersioned {
  row_version: number;
}

/** Standard cursor pagination envelope shared by every list endpoint. */
export interface DirectoryPagination {
  cursor: string;
  has_more: boolean;
}

/** Standard list response envelope shared by every directory endpoint. */
export interface DirectoryListResponse<T> {
  items: T[];
  pagination: DirectoryPagination;
}

/**
 * Page-level result shape returned by every directory query hook.
 * Mirrors the audit feature's typed AuditQueryResult shape so
 * pages can switch on `state` instead of inspecting TanStack fields.
 */
export interface DirectoryQueryResult<T> {
  data: T | null;
  state:
    | "loading"
    | "success"
    | "fetch-failure"
    | "permission-denied"
    | "validation-error"
    | "rate-limit";
  error: DirectoryBackendError | null;
}

// ─── Person ──────────────────────────────────────────────────────────────────

/**
 * A single person record (FR-DIR-1).
 *
 * ISS-518 BA option (a) -- the API client unwraps the Go value
 * blob (internal/directory/person.go:85) into this flat shape;
 * the SPA type stays flat per the BA directive in note 18233.
 *
 * FR-AUTH-7 server-stripped invariant (locked by the
 * apps/web/src/directory/__fixtures__/people.test.ts drift
 * guard): every value.* typed column is OPTIONAL/nullable on
 * the SPA side. The server strips fields the caller is not
 * authorized to see; absence renders as empty, never as an
 * error.
 *
 * The pre-ISS-518 denormalized helpers (title,
 * primary_unit_name, tags) + the primary_*_id family were
 * DROPPED per the BA ruling -- the Go wire never emits them and
 * the spec's "No new backend. If a field or route is missing,
 * comment here before building" forecloses them.
 */
export interface Person extends RowVersioned {
  id: string;
  tenant_id: string;
  /** FR-BPM-10 optimistic pinned definition version (Go TOP-level). */
  definition_version: number;
  /**
   * The BA spec's "only required" -- server-side
   * display_name_required invariant. Optional in the SPA type
   * per the FR-AUTH-7 strip-passes-may-omit rule; the
   * create/edit form layer enforces non-empty on submission.
   */
  display_name?: string;
  /**
   * The remaining typed columns are FR-AUTH-7 server-stripped;
   * every field below is OPTIONAL/nullable on the SPA side.
   */
  legal_name?: string | null;
  date_of_birth?: string | null;
  tax_id?: string | null;
  email?: string | null;
  phone?: string | null;
  locale?: string | null;
  status?: "active" | "inactive" | "archived";
  /** Server keycloak_subject_id (Go) -> keycloak_subject (SPA, renamed per ISS-518). */
  keycloak_subject?: string | null;
  created_at: string;
  updated_at: string;
}
/** A single person contact row (FR-DIR-1 contact sub-resource). */
export interface PersonContact extends RowVersioned {
  id: string;
  person_id: string;
  kind: "email" | "phone" | "address";
  value: string;
  is_primary: boolean;
}

/** A person audit entry (FR-DIR-1 audit sub-resource). */
export interface PersonAuditEntry extends RowVersioned {
  id: string;
  person_id: string;
  action: string;
  author_identity: string;
  agent_principal: string | null;
  version_before: number | null;
  version_after: number | null;
  created_at: string;
}

/**
 * Person summary composite for the /people/:id route (FR-DIR-2).
 *
 * ISS-518 BA option (a): the denormalized helpers that used to
 * live here (`title`, `primary_unit_name`, `tags`) were
 * DROPPED — Go never emits them and the spec forecloses
 * `primary_*_id` backend denormalization. The summary now
 * exposes the same fields as `Person` (plus the sub-resources)
 * with the server-stripped OPTIONAL rule propagated.
 */
export interface PersonSummary {
  person: Person;
  contacts: PersonContact[];
  employment: EmploymentAssignment[];
  qualifications: Qualification[];
  audit: PersonAuditEntry[];
  /**
   * Server-stripped OPTIONAL per FR-AUTH-7 — these may be
   * undefined if the server strip-pass dropped them. The page
   * renders `undefined` as empty (per the BA's "absent field
   * renders as empty, never as an error" directive).
   */
  display_name?: string;
  status?: Person["status"];
}

// ─── Organization Unit ───────────────────────────────────────────────────────

/**
 * Organization unit (FR-DIR-2 organization_unit subpackage).
 *
 * ISS-517 BA option (a) — this interface IS the Go wire. The Go
 * struct lives at `internal/directory/organization_unit/package.go:40-52`
 * and the FR-AUTH-7 strip pass for this entity is a NO-OP
 * (`internal/directory/authorization/strip.go:96-97` returns a nil
 * mask), so all 11 Go keys reach the SPA unchanged.
 *
 * Drift guard: this interface is pinned to the Go wire by the
 * decoder test at `apps/web/src/directory/__fixtures__/organization_units.test.ts`
 * — both at compile time (`keyof OrganizationUnit` must equal the
 * 11-key Go field set exactly) and at runtime (the fixture keys).
 * Any field added or removed here without a corresponding Go struct
 * change FAILS that regression.
 *
 * The pre-ISS-517 SPA carried five phantom fields (`unit_kind`,
 * `member_count`, `legal_entity_name`, `headcount`, `updated_at`)
 * that Go never sent plus the pre-rename `parent_id`. They are the
 * silent-empty list bug class (BA note_17803) and are removed
 * permanently; the FORBIDDEN_LEGACY_FIELDS list in the fixture locks
 * them out.
 *
 * INV-2: the legal-entity NAME is deliberately NOT on this record.
 * Go ships `legal_entity_id` only. The SPA joins the name from
 * `GET /api/directory/legal-entities` at render time (BA note 18335
 * ruling D3) — it never synthesises a denormalised field the server
 * did not emit.
 */
export interface OrganizationUnit extends RowVersioned {
  id: string;
  tenant_id: string;
  /** Every org unit FKs to a legal entity (the hierarchy root). */
  legal_entity_id: string;
  /** Self-FK to the reporting parent. null when the unit IS a root. */
  parent_organization_unit_id: string | null;
  name: string;
  /** Optional operator code (e.g. "PLAT-ENG"). null when unassigned. */
  code: string | null;
  status: "active" | "closed";
  /** Set when status === "closed". null for active records. */
  closed_at: string | null;
  created_at: string;
  /**
   * Authenticated subject who created the row. Go derives it from
   * the identity via `subjectUUID` hashing
   * (`internal/directory/organization_unit/handlers.go:91,340-345`),
   * so it is a stable UUID — never a raw bearer-subclaim string.
   */
  created_by_subject: string;
}

/** A node in the recursive organization tree. */
export interface OrganizationTreeNode {
  unit: OrganizationUnit;
  children: OrganizationTreeNode[];
}

// ─── Position ────────────────────────────────────────────────────────────────

/**
 * Position (FR-DIR-2 position subpackage).
 *
 * ISS-516 BA-corrected wire shape (per the #516 SPA <-> Go diff  // design-adherence-ignore
 * note 17855). The Go struct lives at
 * `internal/directory/position/package.go:22-40` (migration 0081).
 *
 * Drift guard: this interface is pinned to the Go wire by the
 * decoder test at `apps/web/src/directory/__fixtures__/positions.test.ts`.
 * Any field added or removed here without a corresponding Go struct
 * change FAILS the regression. The pre-ISS-516 SPA carried three
 * phantom fields (`role_code`, `unit_name`, `updated_at`) that Go
 * never sent — the silent-empty list bug class. They are removed
 * here; the regression locks them out via FORBIDDEN_LEGACY_FIELDS.
 */
export interface Position extends RowVersioned {
  id: string;
  tenant_id: string;
  organization_unit_id: string | null;
  location_id: string | null;
  title: string;
  /** Position job code (e.g. "FIN-MGR-01"). Wire: Go `job_code`. */
  job_code: string | null;
  /** "open" | "filled" | "closed" (Go wire — per BA enum correction). */
  status: "open" | "filled" | "closed";
  filled_by_person_id: string | null;
  closed_at: string | null;
  created_at: string;
  created_by_subject: string;
}

// ─── Employment Assignment ───────────────────────────────────────────────────

/** Employment assignment (FR-DIR-2 employment_assignment subpackage). */
export interface EmploymentAssignment extends RowVersioned {
  id: string;
  tenant_id: string;
  person_id: string;
  position_id: string;
  organization_unit_id: string;
  start_date: string;
  end_date: string | null;
  assignment_kind: "primary" | "secondary" | "acting" | "historical";
  created_at: string;
  updated_at: string;
  /** Denormalized display helpers (server-joined; INV-2). */
  position_title?: string | null;
  unit_name?: string | null;
}

// ─── Location ────────────────────────────────────────────────────────────────

/**
 * Location (FR-DIR-2 location subpackage).
 *
 * ISS-515 BA option (a) — the SPA Location type is the Go wire
 * contract from `internal/directory/location/package.go:22-58`.
 * The pre-ISS-515 SPA carried four phantom address fields
 * (country_code / city / address_line / country) and two display
 * helpers (kind, updated_at) that Go never emits. The list page
 * silently rendered empty for those (BA note_17803 — the "silent-
 * empty list bug"). The fields are dropped here permanently
 * (locked by the drift-guard test in
 * apps/web/src/directory/fixtures/locations.test.ts) and the four
 * Go wire fields the SPA was missing
 * (parent_location_id / code / closed_at / created_by_subject)
 * are added.
 *
 * ISS-522: `kind` is GAINED as a wire field. The Go repository
 * (`pgLocationStore.Insert`) now writes `kind` (0086's CHECK list
 * is `('site', 'building', 'floor', 'room', 'desk', 'remote',
 * 'virtual')`), the handler defaults an absent request value to
 * `"building"`, and the wire response carries it on every emit.
 * The SPA `Location` interface mirrors the Go wire exactly — the
 * drift-guard fixture + test assert the keys match the
 * `GO_LOCATION_FIELD_SET` exported from `locations.ts`.
 *
 * INV-2: the server is the only authority on what is visible
 * (FR-AUTH-6 / `entity.Authorizer.StripFields`). The SPA MUST
 * NOT synthesise display fields the server did not emit.
 */
export interface Location extends RowVersioned {
  id: string;
  tenant_id: string;
  /** Parent in the hierarchical location layout (campus -> building
   * -> floor -> room). null when the location IS the root. */
  parent_location_id: string | null;
  /**
   * Bounded v1 vocabulary (site | building | floor | room | desk |
   * remote | virtual). Server defaults to "building" on the
   * request side when the caller omits it. Mirrors the Go
   * `Location.Kind` field (ISS-522) and the DB CHECK
   * `location_kind_vocab_chk`.
   */
  kind: string;
  name: string;
  /** Optional operator code (e.g. "BER-01"). null when unassigned. */
  code: string | null;
  status: "active" | "closed";
  /** Set when status === "closed". null for active records. */
  closed_at: string | null;
  row_version: number;
  created_at: string;
  /** Authenticated subject who created the row (per
   * `internal/directory/location/handlers.go:54-59` subjectUUID
   * hashing). */
  created_by_subject: string;
}

// ─── Legal Entity ────────────────────────────────────────────────────────────

/** Legal entity (FR-DIR-2 legal_entity subpackage). */
export interface LegalEntity extends RowVersioned {
  id: string;
  tenant_id: string;
  name: string;
  registration_number: string | null;
  jurisdiction: string;
  created_at: string;
  updated_at: string;
}

// ─── Qualification ───────────────────────────────────────────────────────────

/** A person's qualification (F1 deferred -- endpoint not shipped). */
export interface Qualification extends RowVersioned {
  id: string;
  tenant_id: string;
  person_id: string;
  kind: string;
  issuer: string;
  issued_at: string;
  expires_at: string | null;
}

/** A team membership record (F1 deferred -- endpoint not shipped). */
export interface TeamMembership extends RowVersioned {
  id: string;
  tenant_id: string;
  team_id: string;
  person_id: string;
  role: string;
  joined_at: string;
  left_at: string | null;
}

// ─── Directory Audit ─────────────────────────────────────────────────────────

/** Directory-scoped audit feed (FR-DIR-1 audit sub-resource). */
export interface DirectoryAuditEntry {
  id: string;
  tenant_id: string;
  resource_kind:
    "person" | "organization_unit" | "position" | "employment" | "location";
  resource_id: string;
  action: string;
  author_identity: string;
  agent_principal: string | null;
  version_before: number | null;
  version_after: number | null;
  created_at: string;
  /** Denormalized display helpers (server-joined; INV-2). */
  timestamp?: string;
  actor_id?: string;
}

// ─── Filter / Pagination State ───────────────────────────────────────────────

/** People directory list filter. */
export interface PeopleFilterState {
  query: string;
  status: "all" | "active" | "inactive" | "archived";
  organization_unit_id: string | null;
  cursor: string | null;
}

/** Positions list filter. */
export interface PositionsFilterState {
  organization_unit_id: string | null;
  status: "all" | "active" | "closed";
  cursor: string | null;
}

/**
 * Locations list filter.
 *
 * ISS-515 BA option (a): the `country_code` filter is removed
 * entirely. Go's `internal/directory/location/handlers.go:259-274`
 * List handler does NOT accept a country_code filter — the
 * field never existed on the wire. The pre-ISS-515 SPA filter was
 * a phantom; the list endpoint's parent_location_id +
 * include_closed filters are the only supported filters.
 */
export interface LocationsFilterState {
  status: "all" | "active" | "closed";
  parent_location_id: string | null;
  include_closed: boolean;
  cursor: string | null;
}

/** Directory audit filter. */
export interface DirectoryAuditFilterState {
  resource_kind: "all" | DirectoryAuditEntry["resource_kind"];
  after: string | null;
  before: string | null;
  cursor: string | null;
}

// ─── Empty Filter Helpers ────────────────────────────────────────────────────

export function emptyPeopleFilter(): PeopleFilterState {
  return {
    query: "",
    status: "all",
    organization_unit_id: null,
    cursor: null,
  };
}

export function emptyPositionsFilter(): PositionsFilterState {
  return {
    organization_unit_id: null,
    status: "all",
    cursor: null,
  };
}

export function emptyLocationsFilter(): LocationsFilterState {
  return {
    status: "all",
    parent_location_id: null,
    include_closed: false,
    cursor: null,
  };
}

export function emptyDirectoryAuditFilter(): DirectoryAuditFilterState {
  return {
    resource_kind: "all",
    after: null,
    before: null,
    cursor: null,
  };
}

// ─── Roles ────────────────────────────────────────────────────────────────────

/** The directory role names that appear in the JWT realm_access.roles. */
export type DirectoryRole =
  "directory.access" | "directory.admin" | "directory.auditor";

/** Capabilities derived from roles + tenant config (server-issued signal). */
export interface DirectoryCapabilities {
  /** Whether the user can see the /directory/admin section. */
  adminAccess: boolean;
  /** Whether the user can see the /directory/audit section. */
  auditAccess: boolean;
}

/**
 * The directory sub-section of the sidebar nav manifest. Mirrors
 * NavManifest (apps/web/src/features/cabinet/types.ts) but kept
 * separate so the cabinet module doesn't grow a hard dependency on
 * directory internals.
 */
export interface DirectoryManifest {
  /** Whether the Directory section is rendered at all. */
  directoryAccess: boolean;
  /** Whether the user can see the /directory/admin section. */
  adminAccess: boolean;
  /** Whether the user can see the /directory/audit section. */
  auditAccess: boolean;
}

// ─── Conflict Resolution ─────────────────────────────────────────────────────

/** 409 envelope body returned by the directory mutating endpoints. */
export interface DirectoryConflictEnvelope {
  code: "row_version_mismatch";
  message: string;
  /** The row_version the client sent (echoed back for diagnostics). */
  client_version: number;
  /** The row_version the server holds (the winner). */
  current_version: number;
  server_record: unknown;
}

/** Typed conflict error thrown by the optimistic-update hook. */
export interface DirectoryConflictError {
  kind: "directory_conflict";
  envelope: DirectoryConflictEnvelope;
  resource_kind:
    "person" | "organization_unit" | "position" | "employment" | "location";
  resource_id: string;
}

// ─── Fetch Error Class ───────────────────────────────────────────────────────

/**
 * Page-level state machine for every directory surface (mirrors the
 * audit feature's AuditFetchState vocabulary; FR-UI-CORE-10 AC-4).
 */
export type DirectoryFetchState =
  | "loading"
  | "success"
  | "fetch-failure"
  | "permission-denied"
  | "validation-error"
  | "rate-limit";

/**
 * Per-person qualifications state. F1 FINDING: the per-person
 * qualifications endpoint is NOT shipped yet. The hook returns
 * `deferred` immediately so the page renders the
 * qualifications-not-shipped state without ever calling the missing
 * endpoint. The static-guard test asserts no fetch to
 * /api/directory/qualifications and no import of that route.
 */
export type PersonQualificationsDeferredState =
  | "deferred"
  | "loading"
  | "success"
  | "fetch-failure"
  | "permission-denied"
  | "validation-error"
  | "rate-limit";

export interface DirectoryBackendError {
  kind:
    "fetch-failure" | "permission-denied" | "validation-error" | "rate-limit";
  message: string;
  retriable: boolean;
  retryAfterSeconds?: number;
}
